import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { trpc } from "../lib/api/trpc";

type SearchResultItem = {
  chunkId: string;
  content: string;
  score: number;
  chunkIndex: number;
  metadata: Record<string, unknown>;
  documentId: string;
  documentTitle: string | null;
  documentFilename: string;
};

type SearchResponse = {
  results: SearchResultItem[];
  query: string;
  totalResults: number;
  semanticResults: number;
  keywordResults: number;
  processingTimeMs: number;
};

function ScoreBar({ score }: { score: number }) {
  const percentage = Math.round(score * 100);
  const colorClass =
    score >= 0.7
      ? "bg-green-500"
      : score >= 0.4
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[120px]">
        <div
          className={`h-2 rounded-full ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs font-mono text-gray-600 dark:text-gray-400 w-10">
        {score.toFixed(3)}
      </span>
    </div>
  );
}

function ResultCard({ result }: { result: SearchResultItem }) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = result.content.length > 400;
  const preview = needsTruncation
    ? result.content.slice(0, 400) + "\u2026"
    : result.content;

  return (
    <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-4">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="min-w-0">
          <Link
            to={`/documents/${result.documentId}`}
            className="text-sm font-medium text-accent hover:underline"
          >
            {result.documentTitle || result.documentFilename}
          </Link>
          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
            chunk #{result.chunkIndex}
          </span>
        </div>
        <ScoreBar score={result.score} />
      </div>

      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
        {expanded ? result.content : preview}
      </p>

      {needsTruncation && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-accent hover:underline"
        >
          {expanded ? "Show less" : "Expand"}
        </button>
      )}
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }, (_, i) => (
        <div
          key={i}
          className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-4 animate-pulse"
        >
          <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
          <div className="space-y-2">
            <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-3 w-5/6 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get("q") || "");
  const [activeQuery, setActiveQuery] = useState(searchParams.get("q") || "");
  const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 10);

  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, isError, error } = trpc.search.query.useQuery(
    { query: activeQuery, limit },
    { enabled: !!activeQuery },
  );

  const searchData = data as SearchResponse | undefined;

  const executeSearch = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setActiveQuery(trimmed);
    setSearchParams({ q: trimmed, limit: String(limit) });
  }, [inputValue, limit, setSearchParams]);

  // Auto-trigger search from URL params on mount
  useEffect(() => {
    const q = searchParams.get("q");
    if (q && !activeQuery) {
      setActiveQuery(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") executeSearch();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
          Search
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Diagnostic search — view raw retrieval results with relevance scores
        </p>
      </div>

      {/* Search bar */}
      <div className="flex gap-3 mb-8">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a search query..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-darkmode-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-accent focus:border-accent"
          />
        </div>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-darkmode-800 text-gray-900 dark:text-white text-sm"
        >
          <option value={5}>5 results</option>
          <option value={10}>10 results</option>
          <option value={20}>20 results</option>
        </select>
        <button
          onClick={executeSearch}
          disabled={!inputValue.trim() || isLoading}
          className="px-6 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Search
        </button>
      </div>

      {/* Results area */}
      {!activeQuery && (
        <div className="max-w-2xl mx-auto py-12">
          <div className="text-center mb-8">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter a query to see what the retrieval layer returns.
            </p>
          </div>

          <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              How this works
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This is a diagnostic tool that shows the raw results from the
              retrieval layer. It uses hybrid search — combining semantic
              (meaning-based) and keyword (exact match) search — to find
              relevant document chunks. No AI answer is generated here; use the
              Chat page for that.
            </p>

            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Try these examples
            </h3>
            <div className="space-y-2">
              {[
                {
                  query: "What is the recommended storage temperature?",
                  label: "Natural language question",
                },
                {
                  query: "lysis buffer preparation protocol",
                  label: "Topic keywords",
                },
                {
                  query: "DNBSEQ-G400 calibration",
                  label: "Product-specific term",
                },
              ].map((example) => (
                <button
                  key={example.query}
                  onClick={() => {
                    setInputValue(example.query);
                    setActiveQuery(example.query);
                    setSearchParams({
                      q: example.query,
                      limit: String(limit),
                    });
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-darkmode-700 transition-colors"
                >
                  <span className="text-sm text-accent">{example.query}</span>
                  <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {example.label}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Reading the results
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>
                  <span className="inline-block w-3 h-2 bg-green-500 rounded-sm mr-2" />
                  Score 0.7+ — strong match
                </li>
                <li>
                  <span className="inline-block w-3 h-2 bg-amber-500 rounded-sm mr-2" />
                  Score 0.4–0.69 — partial match
                </li>
                <li>
                  <span className="inline-block w-3 h-2 bg-red-500 rounded-sm mr-2" />
                  Score below 0.4 — weak match
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeQuery && isLoading && <ResultsSkeleton />}

      {activeQuery && isError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-400">
            Search failed: {error?.message || "Unknown error"}
          </p>
        </div>
      )}

      {activeQuery && searchData && (
        <>
          {/* Summary bar */}
          <div className="mb-4 px-4 py-2.5 bg-gray-50 dark:bg-darkmode-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">
                {searchData.totalResults} results
              </span>{" "}
              in {searchData.processingTimeMs}ms — {searchData.semanticResults}{" "}
              semantic, {searchData.keywordResults} keyword
            </p>
          </div>

          {/* Results list */}
          {searchData.results.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No chunks matched this query. This may mean no relevant
                documents are indexed, or the query is too specific.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {searchData.results.map((result) => (
                <ResultCard key={result.chunkId} result={result} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
